import React, { useEffect, useRef, useState } from "react";
import { MinusOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";

function CounterRow({ label, hint, value, min, max, onChange }) {
  return (
    <div className="hrm-occ__row">
      <div className="hrm-occ__labels">
        <span className="hrm-occ__label">{label}</span>
        {hint ? <span className="hrm-occ__hint">{hint}</span> : null}
      </div>
      <div className="hrm-occ__counter" role="group" aria-label={label}>
        <button
          type="button"
          className="hrm-occ__btn"
          aria-label={`הפחתת ${label}`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
        >
          <MinusOutlined />
        </button>
        <span className="hrm-occ__value" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          className="hrm-occ__btn"
          aria-label={`הוספת ${label}`}
          disabled={value >= max}
          onClick={() => onChange(value + 1)}
        >
          <PlusOutlined />
        </button>
      </div>
    </div>
  );
}

function buildLabel({ adults, children, infants, rooms }) {
  const parts = [];
  parts.push(adults === 1 ? "מבוגר אחד" : `${adults} מבוגרים`);
  if (children > 0) parts.push(children === 1 ? "ילד אחד" : `${children} ילדים`);
  if (infants > 0) parts.push(infants === 1 ? "תינוק אחד" : `${infants} תינוקות`);
  parts.push(rooms === 1 ? "חדר אחד" : `${rooms} חדרים`);
  return parts.join(" · ");
}

/**
 * Compact occupancy picker for packages results mockup.
 */
export default function MockOccupancyField({
  adults = 2,
  children = 0,
  infants = 0,
  rooms = 1,
  onChange,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ adults, children, infants, rooms });

  useEffect(() => {
    setDraft({
      adults: Number(adults) || 1,
      children: Number(children) || 0,
      infants: Number(infants) || 0,
      rooms: Number(rooms) || 1,
    });
  }, [adults, children, infants, rooms]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openEditor = () => {
    setDraft({
      adults: Number(adults) || 1,
      children: Number(children) || 0,
      infants: Number(infants) || 0,
      rooms: Number(rooms) || 1,
    });
    setOpen(true);
  };

  const confirm = () => {
    const next = {
      adults: Math.max(1, Number(draft.adults) || 1),
      children: Math.max(0, Number(draft.children) || 0),
      infants: Math.max(0, Number(draft.infants) || 0),
      rooms: Math.max(1, Number(draft.rooms) || 1),
    };
    next.occupancyLabel = buildLabel(next);
    onChange?.(next);
    setOpen(false);
  };

  const triggerLabel = buildLabel({
    adults: Number(adults) || 1,
    children: Number(children) || 0,
    infants: Number(infants) || 0,
    rooms: Number(rooms) || 1,
  });

  return (
    <div className="hrm-picker" ref={rootRef}>
      <button
        type="button"
        className={`hrm-picker__trigger${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openEditor())}
      >
        <TeamOutlined aria-hidden />
        <span>{triggerLabel}</span>
      </button>

      {open ? (
        <div className="hrm-occ" role="dialog" aria-label="הרכב נוסעים וחדרים" dir="rtl">
          <div className="hrm-occ__section">
            <p className="hrm-occ__section-title">חדר 1</p>
            <CounterRow
              label="מבוגרים"
              value={draft.adults}
              min={1}
              max={9}
              onChange={(v) => setDraft((d) => ({ ...d, adults: v }))}
            />
            <CounterRow
              label="ילדים"
              hint="גילאי 2–12"
              value={draft.children}
              min={0}
              max={6}
              onChange={(v) => setDraft((d) => ({ ...d, children: v }))}
            />
            <CounterRow
              label="תינוקות"
              hint="עד גיל 2"
              value={draft.infants}
              min={0}
              max={4}
              onChange={(v) => setDraft((d) => ({ ...d, infants: v }))}
            />
          </div>
          <div className="hrm-occ__section hrm-occ__section--rooms">
            <CounterRow
              label="חדרים"
              value={draft.rooms}
              min={1}
              max={6}
              onChange={(v) => setDraft((d) => ({ ...d, rooms: v }))}
            />
          </div>
          <button type="button" className="hrm-occ__confirm" onClick={confirm}>
            אישור
          </button>
        </div>
      ) : null}
    </div>
  );
}

export { buildLabel as buildOccupancyLabelFromCounts };
